"use client";

import { useState } from "react";
import { Container, BlueButton } from "./Primitives";
import type { CompanyComfortClass } from "@/lib/companySettings";

/**
 * ComfortLevel — табы классов + горизонтальная карусель.
 *
 * Все классы приходят из админ-настроек (`/company/comfort`). Если список
 * пуст — секция не рендерится.
 *
 * Desktop (lg+): 3-card carousel. Активная по центру, prev/next по бокам
 *   меньшие и полупрозрачные.
 * Mobile: только активная картинка, без боковых.
 * Карусель циклическая: last+1 = 0, 0-1 = last.
 */
export default function ComfortLevel({
  classes,
}: {
  classes?: CompanyComfortClass[];
} = {}) {
  const list = classes ?? [];
  // Активный индекс — начинаем с первого. Если массив схлопнулся в админке,
  // safe-clamp ниже не даст выйти за границы.
  const [activeIndex, setActiveIndex] = useState(0);

  if (list.length === 0) return null;
  const activeIdxSafe = Math.min(activeIndex, list.length - 1);

  return (
    <section id="cars" className="pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-[85px] lg:pb-[85px]">
      <Container>
        <h2 className="mb-8 text-center text-[28px] font-medium tracking-[-0.5px] text-text sm:text-[32px] lg:text-[38px] xl:tracking-[-1px] lg:mb-10">
          Уровень комфорта
        </h2>

        {/* Tabs row */}
        <div className="mb-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3 lg:mb-12">
          {list.map((c, i) => {
            const isActive = i === activeIdxSafe;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`rounded-[50px] border px-5 py-2.5 text-[13px] font-medium transition-colors duration-200 sm:text-sm ${
                  isActive
                    ? "border-black bg-black text-white"
                    : "border-black bg-transparent text-black hover:bg-black/5"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </Container>

      {/* Full-width carousel */}
      <div className="relative mb-6 h-[220px] w-full overflow-hidden sm:h-[280px] lg:mb-8 lg:h-[400px]">
        {list.map((c, i) => {
          const n = list.length;
          let d = i - activeIdxSafe;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;

          let cls = "opacity-0 scale-50 -translate-x-[200%]";
          if (d === 0) {
            cls = "opacity-100 scale-100 translate-x-0 z-20";
          } else if (d === -1) {
            cls = "opacity-0 lg:opacity-40 lg:scale-[0.55] lg:-translate-x-[35%] z-10";
          } else if (d === 1) {
            cls = "opacity-0 lg:opacity-40 lg:scale-[0.55] lg:translate-x-[35%] z-10";
          } else if (d < -1) {
            cls = "opacity-0 scale-50 -translate-x-[200%]";
          } else {
            cls = "opacity-0 scale-50 translate-x-[200%]";
          }

          return (
            <div
              key={i}
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-out ${cls}`}
              aria-hidden={d !== 0}
            >
              <div className="relative h-full w-full max-w-[860px] aspect-[860/360]">
                {c.image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={c.image}
                    alt={c.model}
                    className="absolute inset-0 size-full object-contain"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Container>
        <p className="mb-7 text-center text-[18px] font-medium text-text transition-opacity duration-300 sm:text-[20px] lg:text-[24px]">
          {list[activeIdxSafe].model}
        </p>

        <div className="flex justify-center">
          <BlueButton />
        </div>
      </Container>
    </section>
  );
}
